import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AppLayout } from '../../components/layout';
import { blogService, BlogPost } from '../../services/BlogService';
import { SEO } from '../../components/common/SEO';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #1a365d;
  margin-bottom: 40px;
  text-align: center;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 32px;
`;

const PostCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  transition: transform 0.2s;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
  }
`;

const ImagePlaceholder = styled.div`
  height: 200px;
  background: #edf2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-weight: 600;
`;

const Content = styled.div`
  padding: 24px;
  flex-grow: 1;
`;

const PostTitle = styled.h2`
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 12px;
  line-height: 1.3;
`;

const Excerpt = styled.p`
  color: #4a5568;
  font-size: 0.95rem;
  margin-bottom: 20px;
`;

const Meta = styled.div`
  font-size: 0.85rem;
  color: #718096;
  display: flex;
  justify-content: space-between;
`;

const ReadMore = styled(Link)`
  color: #3182ce;
  font-weight: 600;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

export const BlogList = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      const publishedPosts = await blogService.getPublishedPosts();
      setPosts(publishedPosts);
      setLoading(false);
    };
    fetchPosts();
  }, []);

  if (loading) return <AppLayout><div>Loading blog...</div></AppLayout>;

  return (
    <AppLayout>
      <SEO 
        title="Blog"
        description="Latest insights, tips, and news from the Nexis ERP team. Stay updated on enterprise automation and field sales optimization."
      />
      <Container>
        <Title>Latest from our Blog</Title>
        {posts.length === 0 ? (
          <p style={{ textAlign: 'center' }}>No posts found. Check back later!</p>
        ) : (
          <BlogGrid>
            {posts.map(post => (
              <PostCard key={post.id}>
                <ImagePlaceholder>
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} /> : 'ERP Insights'}
                </ImagePlaceholder>
                <Content>
                  <PostTitle>{post.title}</PostTitle>
                  <Excerpt>{post.excerpt}</Excerpt>
                  <Meta>
                    <span>{post.publishedAt?.toDate().toLocaleDateString()}</span>
                    <ReadMore to={`/blog/${post.slug}`}>Read More →</ReadMore>
                  </Meta>
                </Content>
              </PostCard>
            ))}
          </BlogGrid>
        )}
      </Container>
    </AppLayout>
  );
};
